<template>
  <v-container>
    <loading
      :active="loader"
      :is-full-page="true"
      :opacity="0.9"
      color="#ff6b00"
    />

    <v-alert
      v-if="alertMsg.status"
      :timeout="alertMsg.timeout"
      :color="alertMsg.color"
      :type="alertMsg.type"
      dismissible
    >
      {{ alertMsg.text }}
    </v-alert>

    <v-row>
      <!-- Bank Name -->
      <v-col cols="12" md="6">
        <label>
          {{ $t("bank_name_business") }}
          <span style="color: #f00">*</span>
        </label>

        <v-autocomplete
          v-model="addProfilePayload.bank_name"
          :placeholder="$t('bank_name_placeholder')"
          :items="bank_names"
          required
          outlined
          dense
          :error-messages="banknameerrors"
          @input="$v.addProfilePayload.bank_name.$touch()"
          @blur="$v.addProfilePayload.bank_name.$touch()"
        ></v-autocomplete>
      </v-col>

      <!-- Bank Account Number -->
      <v-col cols="12" md="6">
        <label>
          {{ $t("bank_account_no_business") }}
          <span style="color: #f00">*</span>
        </label>

        <v-text-field
          v-model="addProfilePayload.bank_account_number"
          :placeholder="$t('bank_account_no_placeholder')"
          dense
          outlined
          required
          :error-messages="accountnumber"
          @input="$v.addProfilePayload.bank_account_number.$touch()"
          @blur="$v.addProfilePayload.bank_account_number.$touch()"
        ></v-text-field>
      </v-col>

      <!-- Save Button -->
      <v-col cols="12" md="12">
        <v-btn
          class="float-right mt-5 px-5 btn-primary-outlined text-capitalize"
          color="primary"
          :loading="loader"
          :disabled="loader"
          @click="onClickNext"
        >
          {{ $t("Save") }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Loading from "vue-loading-overlay";
import AgentService from "@/services/agentService/AgentService";
import commonService from "@/services/AgentCommonService/commonService.js";
import { required, numeric } from "vuelidate/lib/validators";

export default {
  name: "BankDetails",

  components: {
    Loading,
  },

  props: {
    isActive: {
      type: Boolean,
      default: true,
    },
  },

  data: () => ({
    bank_names: [],

    loader: false,

    addProfilePayload: {
      bank_name: "",
      bank_account_number: "",
    },

    alertMsg: {
      status: false,
      text: "",
      timeout: 2000,
      color: "blue-grey",
      type: "info",
    },
  }),

  validations() {
    return {
      addProfilePayload: {
        bank_name: {
          required,
        },
        bank_account_number: {
          required,
          numeric,
        },
      },
    };
  },

  computed: {
    banknameerrors() {
      const errors = [];

      if (!this.$v.addProfilePayload.bank_name.$dirty) {
        return errors;
      }

      if (!this.$v.addProfilePayload.bank_name.required) {
        errors.push(this.$t("bank_name_required"));
      }

      return errors;
    },

    accountnumber() {
      const errors = [];

      if (!this.$v.addProfilePayload.bank_account_number.$dirty) {
        return errors;
      }

      if (!this.$v.addProfilePayload.bank_account_number.required) {
        errors.push(this.$t("bank_acc_numb_req"));
      }

      if (
        this.$v.addProfilePayload.bank_account_number.required &&
        !this.$v.addProfilePayload.bank_account_number.numeric
      ) {
        errors.push(this.$t("only_type_numb"));
      }

      return errors;
    },
  },

  watch: {
    isActive(active) {
      if (active) {
        this.getOtherData();
      }
    },
  },

  created() {
    this.getOtherData();
  },

  methods: {
    /**
     * Get previously saved bank details.
     */
    async getOtherData() {
      if (!this.$route.query.login_id || !this.$route.query.customer_id || !this.isActive) return;
      try {
        const country = JSON.parse(sessionStorage.getItem("partner_country") || "null");
        if (country?.iso_alpha3) await this.getpayLocation("B", country.iso_alpha3);
        const response = await AgentService.getOthersData({
          login_id: this.$route.query.login_id,
          customer_id: this.$route.query.customer_id,
        });
        if (Number(response?.data?.status_code) !== 200) {
          throw new Error(response?.data?.message || "Unable to load bank details.");
        }
        const otherData = response?.data?.data?.[0] || {};
        this.addProfilePayload = {
          ...this.addProfilePayload,
          bank_name: otherData.bank_name || "",
          bank_account_number: otherData.bank_account_number != null
            ? String(otherData.bank_account_number)
            : "",
        };
        if (this.addProfilePayload.bank_name && !this.bank_names.includes(this.addProfilePayload.bank_name)) {
          this.bank_names.push(this.addProfilePayload.bank_name);
        }
        this.$nextTick(() => this.$v.$reset());
      } catch (error) {
        this.alertMsg = {
          ...this.alertMsg,
          status: true,
          text: error?.message || "Unable to load bank details. Please try again.",
          type: "error",
          color: "error",
        };
      }
    },

    /**
     * Validate and save bank details.
     */
    async onClickNext() {
      // Prevent multiple Save clicks.
      if (this.loader) {
        return;
      }

      this.$v.$touch();

      // Stop if form is invalid.
      if (this.$v.$invalid) {

        return;
      }

      const saved = await this.postList();

      // Move to next step only after successful save.
      if (saved) {
        this.$emit("onNextClick", {
          ...this.addProfilePayload,
        });
      }
    },

    /**
     * Back button handler.
     */
    onClickBack() {
      this.$emit("onClickBack");
    },

    /**
     * Get bank names based on selected country.
     */
    async getpayLocation(payment_mode, selected_country) {
      this.bank_names = [];
      try {
        if (!selected_country) {
          return;
        }

        const responseData = await commonService.getbankList({
          country: selected_country,
          payment_mode,
        });

        if (responseData?.status === 200) {
          const locations = responseData?.data?.data || [];

          if (Array.isArray(locations)) {
            this.bank_names = locations
              .map((item) => item.locationName)
              .filter(Boolean);
          }
        }
      } catch (error) {
        console.error("Error getting bank locations:", error);
      }
    },

    /**
     * Save bank details.
     */
    async postList() {
      // Prevent duplicate API calls.
      if (this.loader) {
        return false;
      }

      this.loader = true;

      // Clear previous alert.
      this.alertMsg.status = false;

      try {
        const payload = {
          ...this.addProfilePayload,
        };

        const responseData = await AgentService.addOtherData(payload, {
          login_id: this.$route.query.login_id,
          customer_id: this.$route.query.customer_id,
        });

        if (responseData?.data?.status_code === 200) {
          this.alertMsg.status = true;
          this.alertMsg.text =
            responseData?.data?.message || "Bank details saved successfully.";
          this.alertMsg.type = "success";
          this.alertMsg.color = "green";

          return true;
        }

        this.alertMsg.status = true;
        this.alertMsg.text = `Failed: ${
          responseData?.data?.message || "Unable to save bank details."
        }`;
        this.alertMsg.type = "error";
        this.alertMsg.color = "warning";

        return false;
      } catch (error) {
        console.error("Error saving bank details:", error);

        this.alertMsg.status = true;

        this.alertMsg.text =
          error?.response?.data?.message ||
          "Error: Something went wrong. Please try again later.";

        this.alertMsg.type = "error";
        this.alertMsg.color = "error";

        return false;
      } finally {
        // This always runs, whether API succeeds or fails.
        this.loader = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/global.scss";

.invalid-feedback-name {
  display: flex;
  flex-direction: column;
  text-align: initial;
  font-size: 13px;
  background-color: #f5f5f5;
  color: #dc3545;
}
</style>
